import { useLayoutEffect, useRef } from "react";
/**
 * Die Wege hinaus aus einem `me-dialog`, so wie die Kundeninfo sie braucht (ME-Etappe 3,
 * Aufgabe 1) — gemeinsam für `Modal` und die Verwerfen-Rückfrage.
 *
 * Warum nicht einfach `onAbbrechen`/`onGeschlossen` der React-Hülle: Beide Ereignisse
 * (`me-abbrechen`, `me-geschlossen`) sind `bubbles` + `composed`, und die Hülle horcht am
 * eigenen Element, ohne nach dem Absender zu fragen. Steht ein Subdialog IM Inhalt eines
 * offenen Modals (AdressListe in AngebotDetail, die Rückfrage im Modal, …), käme sein
 * Abbrechen beim Eltern-Modal als dessen eigenes an. Hier zählt deshalb nur, was am
 * eigenen Host ausgelöst wurde (`target === host`).
 *
 * Und Escape: Das Element horcht an seiner nativen Ebene auf `keydown` und fragt dabei
 * nicht nach `defaultPrevented`. Daraus folgen zwei Riegel am Host:
 *
 *  - **Capture:** Hat eine Schicht IM Dialog das Escape schon verbraucht (der gemeinsame
 *    Escape-Stapel horcht in der Capture-Phase am Dokument — Ziehen einer Position, die
 *    Belegvorschau), kommt es bei der Ebene gar nicht mehr an. Sonst bräche ein Escape,
 *    das nur das Ziehen beenden soll, zugleich die Maske ab.
 *  - **Bubble:** Ein Escape, das dieser Dialog behandelt hat, endet an seinem Host
 *    (`preventDefault` UND `stopPropagation`). Ohne das liefe es weiter zur Ebene des
 *    Eltern-Dialogs — der ist in diesem Augenblick schon wieder oberster (der Subdialog hat
 *    sich gerade geschlossen) und bräche dann ebenfalls ab. Das `preventDefault` sagt den
 *    Horchern am Dokument (me-spalten faltet sonst seine überlagernde Kontextspalte), dass
 *    das Escape verbraucht ist — dieselbe Absprache wie beim Escape-Stapel.
 *
 * Der Host wird über einen Anker im Titel gefunden (`closest("me-dialog")`): Die
 * React-Hülle gibt über ihre Ref nur die Steuerung heraus, nicht das Element.
 */
export function useDialogWege(anker, wege) {
    const aktuell = useRef(wege);
    aktuell.current = wege;
    // Layout-Effekt: Die Riegel hängen, bevor der Dialog (ebenfalls im Layout-Effekt) öffnet.
    useLayoutEffect(() => {
        const host = anker.current?.closest("me-dialog") ?? null;
        if (host === null)
            return undefined;
        const abbrechen = (ereignis) => {
            if (ereignis.target !== host)
                return;
            aktuell.current.beiAbbrechen(ereignis);
        };
        const geschlossen = (ereignis) => {
            if (ereignis.target !== host)
                return;
            aktuell.current.beiGeschlossen?.();
        };
        const escapeVorher = (ereignis) => {
            if (ereignis.key !== "Escape")
                return;
            if (ereignis.defaultPrevented || ereignis.isComposing)
                ereignis.stopPropagation();
        };
        const escapeNachher = (ereignis) => {
            if (ereignis.key !== "Escape")
                return;
            ereignis.preventDefault();
            ereignis.stopPropagation();
        };
        host.addEventListener("me-abbrechen", abbrechen);
        host.addEventListener("me-geschlossen", geschlossen);
        host.addEventListener("keydown", escapeVorher, true);
        host.addEventListener("keydown", escapeNachher);
        return () => {
            host.removeEventListener("me-abbrechen", abbrechen);
            host.removeEventListener("me-geschlossen", geschlossen);
            host.removeEventListener("keydown", escapeVorher, true);
            host.removeEventListener("keydown", escapeNachher);
        };
    }, [anker]);
}
//# sourceMappingURL=dialogWege.js.map